import * as React from 'react';
import { getDic } from '../../assets/i18n/dictionary';
import { store } from '../../store';
import * as actions from '../../store/actions';
import {
  Alert,
  Loading,
  Container,
  IsLoadingBackdrop,
} from '../../components';
import { useNavigate } from "react-router-dom";
import faceApiService from '../../services/faceApiService';
import fileAwsService from '../../services/fileAwsService';
import faceScanConfigService from '../../services/faceScanConfigService';
import urlUtils from '../../utils/urlUtils';
import moment from 'moment';

import EmbedInstructions from './EmbedInstructions';
import EndMessage from './EndMessage';
import PreForm from './PreForm';
import Step2SendImageForm from './Step2SendImageForm';
import Step1Instructions from './Step1Instructions';
import Step2CameraPhoto from './Step2CameraPhoto';
import Step4EndMessage from './Step4EndMessage';
import StepHeader from './StepHeader';
import LanguageSelection from './LanguageSelection';

function getCurrentDimension() {
  return {
    width: window.innerWidth,
    height: window.innerHeight
  }
}

const FaceCollectorAwsPage = (props) => {

  const navigate = useNavigate();
  const params = urlUtils.getSearchParams();
  const [reRender, setReRender] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);
  const [dimensions, setDimensions] = React.useState(getCurrentDimension());
  const [loadingControl, setLoadingControl] = React.useState({
    open: true,
    message: getDic("carregando")
  });
  const [alertControl, setAlertControl] = React.useState({
    open: false,
    title: '',
    message: '',
    icon: 'info',
  });
  const [configOptions, setConfigOptions] = React.useState({
    backgroundImage: '',
    backgroundColor: '#f2f2f2',
    textColor: 'black',
    buttonBackgroundColor: '#456091',
    buttonTextColor: 'white',
    eventId: '',
    miceContractId: '',
    photoAttributeId: '',
  });
  const [faceScanControl, setFaceScanControl] = React.useState({
    eventId: '',
    system: '',
    guestId: '',
    score: 95,
    openPreForm: false,
    openCamera: false,
  });
  const [currentStep, setCurrentStep] = React.useState(1);
  const [cameraControl, setCameraControl] = React.useState({
    hasPermission: false,
    cameraAvailable: false,
    errorMessage: '',
  });

  React.useEffect(() => {
    const updateDimension = () => {
      setDimensions(getCurrentDimension())
    }
    window.addEventListener('resize', updateDimension);
    return (() => {
      window.removeEventListener('resize', updateDimension);
    })
  }, [dimensions]);

  React.useEffect(() => {
    getPageData();
    checkCamera();
  }, []);

  const getPageData = React.useCallback(async () => {
    try {
      if ((!store.getState().user.id || !store.getState().user.email) && params?.public !== 'true') {
        navigate('/Login', { replace: true });
      }

      getLanguage();

      const _faceScanControl = getPreForm();
      await tryPreSearchCurrentDetection(_faceScanControl);
      await getConfig(_faceScanControl);

    } catch (error) {
      console.log('Error getPageData', error);
      setAlertControl({
        open: true,
        title: getDic("erro"),
        message: getDic("enviado-erro"),
        icon: 'error'
      });
    } finally {
      setLoadingControl({ ...loadingControl, open: false });
      setIsLoading(false);
    }

    function getLanguage() {
      const _lang = params?.lang || params?.Lang || '';
      switch (_lang) {
        case 'en':
          store.dispatch(actions.setLanguage({ lang: _lang }));
          break;
        case 'sp':
          store.dispatch(actions.setLanguage({ lang: _lang }));
          break;
        default:
          store.dispatch(actions.setLanguage({ lang: 'pt-br' }));
          break;
      }
    }

    function getPreForm() {
      try {
        let _faceScanControl = { ...faceScanControl };

        if (
          params?.public === 'true' && (
            !params?.eventId ||
            !params?.guestId ||
            !params?.system ||
            !params?.attributeId
          )) {
          navigate('/Login', { replace: true });
        }

        if (params?.eventId) {
          _faceScanControl.eventId = params.eventId;
        }
        if (params?.system) {
          _faceScanControl.system = params.system;
        }
        if (params?.guestId) {
          _faceScanControl.guestId = params.guestId;
        }
        if (params?.attributeId) {
          _faceScanControl.attributeId = params.attributeId;
        }
        if (params?.score) {
          _faceScanControl.score = parseInt(params.score);
        }

        if (!_faceScanControl.eventId || !_faceScanControl.system || !_faceScanControl.guestId) {
          _faceScanControl.openPreForm = true;
        } else {
          _faceScanControl.openCamera = true;
        }

        setFaceScanControl(_faceScanControl);
        return _faceScanControl;
      } catch (error) {
        console.log('Error getPreForm', error);
        throw error;
      }
    }

    async function tryPreSearchCurrentDetection(faceScanControl) {
      try {
        if (
          faceScanControl.eventId &&
          faceScanControl.system &&
          faceScanControl.guestId
        ) {
          const _faceDetection = await faceApiService.getFaceDetectionByGuestAndEventId(
            faceScanControl.eventId,
            faceScanControl.system,
            faceScanControl.guestId,
          );

          if (_faceDetection && _faceDetection.id) {
            setCurrentStep(4);
          }
        }
      } catch (error) {
        console.log('Error tryPreSearchCurrentDetection', error);
      }
    }

    async function getConfig(faceScanControl) {
      if (
        !faceScanControl.eventId ||
        !faceScanControl.system
      ) {
        return;
      }

      try {
        const _ret = await faceScanConfigService.getByEventId(
          faceScanControl.eventId,
          faceScanControl.system,
        );

        if (_ret.id) {
          setConfigOptions(_ret);
        }
      } catch (error) {
        console.log('Error getConfig', error);
      }
    }
  }, []);

  const checkCamera = React.useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasVideoInput = devices.some(d => d.kind === "videoinput");

      if (!hasVideoInput) {
        setCameraControl({
          ...cameraControl,
          errorMessage: "Nenhuma câmera detectada.",
          cameraAvailable: false,
        });
        return;
      }

      // Solicita permissão explicitamente
      await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraControl({
        ...cameraControl,
        cameraAvailable: true,
        hasPermission: true,
      });
    } catch (err) {
      console.error("Erro ao acessar câmera:", err);
      setCameraControl({
        ...cameraControl,
        errorMessage: "Não foi possível acessar a câmera. Verifique as permissões do navegador.",
        hasPermission: false
      });
    }
  }, []);

  const tryOpenCamera = React.useCallback(async () => {
    try {
      let _errorMessage = validateForm();
      if (_errorMessage) {
        setAlertControl({
          open: true,
          title: '',
          message: _errorMessage,
          icon: 'danger'
        });
        return;
      }

      setFaceScanControl({
        ...faceScanControl,
        openPreForm: false,
        openCamera: true,
      });

    } catch (error) {
      console.log('Error tryOpenCamera', error);
      setAlertControl({
        open: true,
        title: getDic("erro"),
        message: getDic("enviado-erro"),
        icon: 'error'
      });
    }

    function validateForm() {
      if (!faceScanControl.eventId) {
        return `${getDic("campo")} "ID ${getDic("evento")}" ${getDic("obrigatorio")}`;
      }
      if (!faceScanControl.system) {
        return `${getDic("campo")} "${getDic("sistema")}" ${getDic("obrigatorio")}`;
      }
      if (!faceScanControl.guestId) {
        return `${getDic("campo")} "ID ${getDic("participante")}" ${getDic("obrigatorio")}`;
      }
    }

  }, [faceScanControl]);

  const tryCaptureFace = React.useCallback(async (image = '') => {
    try {
      setLoadingControl({ ...loadingControl, open: true, message: getDic("salvando") });
      // Adicionar tratativa para compactar imagem
      let _detectionId = await faceApiService.validateFaceDetectionFromFile(image);

      await faceApiService.addGuestToFaceDetection(
        _detectionId,
        params.eventId,
        params.system,
        params.guestId,
      );

      await trySaveImageLog();

      setFaceScanControl({
        ...faceScanControl,
        openPreForm: false,
        openCamera: false,
      });

      setCurrentStep(3);

      window.top.postMessage(JSON.stringify({
        message: 'faceDetectionCollected',
        data: _detectionId
      }), '*');

    } catch (error) {
      console.log('Error tryCaptureFace', error);
      let _errorObj = errorHandler(error);
      setAlertControl(_errorObj);
    } finally {
      setLoadingControl({ ...loadingControl, open: false });
    }

    function errorHandler(error) {
      let _errorObj = {
        open: true,
        title: '',
        message: '',
        icon: 'danger',
      }

      switch (error?.response?.data?.message) {
        case 'NO_FACE_DETECTED':
          _errorObj.message = `Não há rosto na imagem`;
          break;
        case 'IMAGE_WITH_MORE_THAN_ONE_FACE':
          _errorObj.message = 'A imagem possui mais de um rosto';
          break;
        case 'FACE_TOO_SMALL':
          _errorObj.message = 'Se aproxime mais da câmera';
          break;
        case 'IMAGE_WITH_LOW_QUALITY':
          _errorObj.message = 'Fique em um local com iluminação adequada';
          break;
        default:
          _errorObj = {
            open: true,
            title: getDic("erro"),
            message: getDic("enviado-erro"),
            icon: 'error',
          }
          break;
      }
      return _errorObj;
    }

    async function trySaveImageLog() {
      try {
        if (!configOptions.photoAttributeId || !configOptions.miceContractId || configOptions.eventId !== faceScanControl.eventId) {
          return;
        }

        let _awsToken = await fileAwsService.getToken();
        const _fileUrl = await fileAwsService.uploadFileToS3AndCreateMetadata(
          _awsToken,
          parseInt(configOptions.miceContractId),
          faceScanControl.guestId,
          image,
        );

        const _url = await fileAwsService.getFileUrl(
          _awsToken,
          'bucket.aws.inteegra/2025',
          _fileUrl.split('2025/')[1].split('?')[0],
          moment().add(6, 'days').toISOString(),
        );

        await faceApiService.updatePhotoAttributeValue(
          faceScanControl.guestId,
          configOptions.photoAttributeId,
          _url,
        );
      } catch (error) {
        console.log('Error trySaveImageLog', error);
      }
    }
  }, [faceScanControl, configOptions]);

  if (isLoading) {
    return (<IsLoadingBackdrop />)
  }
  return (
    <Container
      header={params?.public === 'true' ? null : `${getDic("capturar")} ${getDic("rosto")}`}
      padding={10}
      background={configOptions.backgroundImage ? 'transparent' : configOptions.backgroundColor}
      backgroundImage={configOptions.backgroundImage}
      backButton={params?.public === 'true' ? false : true}
      scroll
    >
      <StepHeader
        dimensions={dimensions}
        configOptions={configOptions}
        currentStep={currentStep}
      />
      <LanguageSelection
        dimensions={dimensions}
        currentStep={currentStep}
        reRender={reRender}
        setReRender={setReRender}
      />
      {params?.public !== 'true' && !faceScanControl.openCamera && (
        <EmbedInstructions
          dimensions={dimensions}
        />
      )}
      {faceScanControl.openPreForm && (
        <PreForm
          dimensions={dimensions}
          faceScanControl={faceScanControl}
          setFaceScanControl={setFaceScanControl}
          tryOpenCamera={tryOpenCamera}
        />
      )}
      {faceScanControl.openCamera && (
        <>
          {currentStep === 0 && (
            <Step1Instructions
              dimensions={dimensions}
              configOptions={configOptions}
              setCurrentStep={setCurrentStep}
            />
          )}
          {currentStep === 1 && (
            <Step1Instructions
              dimensions={dimensions}
              configOptions={configOptions}
              setCurrentStep={setCurrentStep}
            />
          )}
          {currentStep === 2 && (
            <>
              {cameraControl.cameraAvailable && cameraControl.hasPermission ? (
                <Step2CameraPhoto
                  params={params}
                  dimensions={dimensions}
                  configOptions={configOptions}
                  tryCaptureFace={tryCaptureFace}
                  cameraControl={cameraControl}
                />
              ) : (
                <Step2SendImageForm
                  dimensions={dimensions}
                  configOptions={configOptions}
                  tryCaptureFace={tryCaptureFace}
                />
              )}
            </>
          )}
          {currentStep === 4 && (
            <Step4EndMessage
              dimensions={dimensions}
              configOptions={configOptions}
              setCurrentStep={setCurrentStep}
            />
          )}
        </>
      )}
      {!loadingControl.open && params?.public === 'true' && !faceScanControl.openPreForm && !faceScanControl.openCamera && (
        <EndMessage
          dimensions={dimensions}
          configOptions={configOptions}
        />
      )}

      {modalControl()}
    </Container>
  );

  function modalControl() {
    if (alertControl.open) {
      return (
        <Alert
          open={alertControl.open}
          title={alertControl.title}
          message={alertControl.message}
          icon={alertControl.icon}
          onPress={() => setAlertControl({ ...alertControl, open: false, icon: 'info' })}
        />
      )
    }
    if (loadingControl.open) {
      return <Loading open={loadingControl.open} loadingMessage={loadingControl.message} />
    }

    return null;
  }
};

export default FaceCollectorAwsPage;
